/**
 * Capability detection for the Local SQLite connector. v1.2 is
 * OPFS-backed (Origin Private File System, `opfs-sahpool` VFS), which
 * stacks one extra runtime requirement on top of the v1 FSA-API check:
 *
 *  1. `showOpenFilePicker` (FSA API) — the user-gesture file picker.
 *  2. `navigator.storage.getDirectory` — the OPFS root accessor.
 *
 * Both are present in modern desktop Chromium engines (Chrome, Edge,
 * Brave, Arc, Opera). Safari < 17 and Firefox fail (1) at v1.2 ship;
 * later versions may add support engine by engine. Detecting via the
 * actual APIs rather than user-agent strings keeps us correct as the
 * matrix changes.
 *
 * The `opfs-sahpool` VFS reads and writes via
 * `FileSystemSyncAccessHandle` directly — synchronous file I/O,
 * available in any dedicated worker — and does NOT require
 * `SharedArrayBuffer` or cross-origin isolation.
 *
 * The UI uses [[checkOpfsCapable]] for the rich "why is this disabled"
 * surface and [[isLocalSqliteSupported]] as a cheap boolean shortcut.
 */

export type OpfsCapability =
  | { ok: true }
  | {
      ok: false;
      reason: "no-fsa" | "no-opfs";
      message: string;
    };

const CHROMIUM_BROWSER_HINT =
  "Local SQLite requires a Chromium-based browser (Chrome, Edge, Arc, Brave) on a desktop OS.";

/** Detailed capability check used by the connector setup UI. Returns a
 *  discriminated union so the UI can surface a precise reason without
 *  guessing.
 *
 *  Synchronous and side-effect-free; the first failing condition is
 *  reported (FSA → OPFS). The order matches the order users hit them
 *  when something's wrong.
 */
export function checkOpfsCapable(): OpfsCapability {
  if (typeof window === "undefined" || !("showOpenFilePicker" in window)) {
    return {
      ok: false,
      reason: "no-fsa",
      message: `Your browser doesn't support the File System Access API. ${CHROMIUM_BROWSER_HINT}`,
    };
  }
  if (
    typeof navigator === "undefined" ||
    !("storage" in navigator) ||
    typeof navigator.storage.getDirectory !== "function"
  ) {
    return {
      ok: false,
      reason: "no-opfs",
      message: `Your browser doesn't support OPFS. ${CHROMIUM_BROWSER_HINT}`,
    };
  }
  return { ok: true };
}

/** Cheap boolean wrapper around [[checkOpfsCapable]]. Use this when
 *  rendering a binary "show the attach button or hide it" decision; use
 *  [[checkOpfsCapable]] when surfacing the reason to the user. */
export function isLocalSqliteSupported(): boolean {
  return checkOpfsCapable().ok;
}
